import * as React from 'react';
import { Link, useLocation } from 'react-router';
import { BreadcrumbsBlock } from 'components';
import {
  LocalShipping,
  Gavel,
  Policy,
  Replay,
  RuleFolder,
} from '@mui/icons-material';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import { APP_ROUTES } from 'constants/routes';
import { TLegalPolicies } from './LegalPolicies.types';
import { LEGAL_POLICY_GROUPS } from './LegalPolicies.constants';
import './LegalPolicies.styled.scss';

export const LegalPolicies: TLegalPolicies = () => {
  const localize = useLocalize();
  const { hash } = useLocation();

  React.useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const sectionElement = document.getElementById(id);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);

  const getPolicyIcon = (policyId: string) => {
    if (policyId === 'privacy-policy') return <Policy fontSize="small" />;
    if (policyId === 'terms-of-service') return <Gavel fontSize="small" />;
    if (policyId === 'refund-return-policy') return <Replay fontSize="small" />;
    if (policyId === 'complaints-warranty-policy')
      return <RuleFolder fontSize="small" />;
    return <LocalShipping fontSize="small" />;
  };

  return (
    <section className="legal-policies">
      <div className="legal-policies__container">
        <div className="legal-policies__content">
          <BreadcrumbsBlock />
          <div className="legal-policies__surface">
            <h3 className="legal-policies__title">
              {localize('legal.policies.page.title')}
            </h3>
            <div className="legal-policies__cards">
              {LEGAL_POLICY_GROUPS.map((policy, index) => (
                <Link
                  key={policy.id}
                  to={`${APP_ROUTES.LEGAL_POLICIES}#${policy.id}`}
                  className="legal-policies__card body1"
                  aria-current={hash === `#${policy.id}` ? 'page' : undefined}
                >
                  <span className="legal-policies__card-title">
                    {getPolicyIcon(policy.id)}
                    {index + 1}. {localize(policy.titleKey)}
                  </span>
                </Link>
              ))}
            </div>

            {LEGAL_POLICY_GROUPS.map((policy, policyIndex) => (
              <article
                key={policy.id}
                id={policy.id}
                className="legal-policies__policy"
              >
                <h4 className="legal-policies__policy-title">
                  {policyIndex + 1}. {localize(policy.titleKey)}
                </h4>
                {policy.sections.map((section) => (
                  <section
                    key={section.key}
                    className="legal-policies__section"
                  >
                    <h5 className="legal-policies__section-title">
                      {localize(`${policy.sectionPrefix}.${section.key}.title`)}
                    </h5>
                    {section.paragraphs?.map((lineKey) => (
                      <p
                        key={lineKey}
                        className="legal-policies__section-text body1"
                      >
                        {localize(
                          `${policy.sectionPrefix}.${section.key}.${lineKey}`
                        )}
                      </p>
                    ))}
                    {section.bullets?.length ? (
                      <ul className="legal-policies__section-list">
                        {section.bullets.map((itemKey) => (
                          <li
                            key={itemKey}
                            className="legal-policies__section-item body1"
                          >
                            {localize(
                              `${policy.sectionPrefix}.${section.key}.${itemKey}`
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </section>
                ))}
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
